/**
 * Estimating Utility Functions
 * 
 * Helper functions for estimate calculations, validations, and formatting.
 * Extracted from EstimatingPage.js for maintainability.
 */

import { 
  MARKET_AVERAGES, 
  FORBIDDEN_PHRASES, 
  LAZY_PHRASE_CORRECTIONS,
  LABOR_KEYWORDS,
  MATERIAL_OVERRIDE_KEYWORDS,
  SCOPE_ACTION_KEYWORDS
} from '../constants';
import { SCOPE_TEMPLATES } from '../constants/scopeTemplates';

/**
 * Find matching template based on message content
 */
export function findMatchingTemplate(message) {
  if (!message) return null;
  const lowerMsg = message.toLowerCase();
  
  for (const [key, template] of Object.entries(SCOPE_TEMPLATES)) {
    for (const trigger of template.triggers) {
      if (lowerMsg.includes(trigger)) {
        return { key, ...template };
      }
    }
  }
  return null;
}

/**
 * Check if message is a new build request
 */
export function isNewBuildRequest(message) {
  if (!message) return false;
  const lowerMsg = message.toLowerCase();
  const newBuildTriggers = [
    'new build', 'new construction', 'ground up', 'from scratch',
    'new home', 'new house', 'build new', 'construct new'
  ];
  return newBuildTriggers.some(trigger => lowerMsg.includes(trigger));
}

/**
 * Clean section name for display
 */
export function cleanSectionName(name) {
  if (!name) return 'General';
  return name
    .replace(/^\d+\.\s*/, '') // Remove leading numbers
    .replace(/[_-]/g, ' ')    // Replace underscores/hyphens with spaces
    .replace(/\s+/g, ' ')     // Normalize whitespace
    .trim();
}

/**
 * Format section name with index
 */
export function formatSectionName(index, rawName) {
  const cleaned = cleanSectionName(rawName);
  return `${index + 1}. ${cleaned}`;
}

/**
 * Detect cost type based on description keywords
 */
export function detectCostType(description) {
  if (!description) return 'Material';
  const lowerDesc = description.toLowerCase();
  
  // Check labor keywords first (more specific)
  for (const keyword of LABOR_KEYWORDS) {
    if (lowerDesc.includes(keyword)) {
      return 'Labor';
    }
  }
  
  // Check material override keywords
  for (const keyword of MATERIAL_OVERRIDE_KEYWORDS) {
    if (lowerDesc.includes(keyword)) {
      return 'Material';
    }
  }
  
  // Default to Material
  return 'Material';
}

/**
 * Estimate market price based on item name
 */
export function estimateMarketPrice(itemName) {
  if (!itemName) return null;
  const lowerName = itemName.toLowerCase();
  
  for (const [keyword, price] of Object.entries(MARKET_AVERAGES)) {
    if (lowerName.includes(keyword)) {
      return price;
    }
  }
  
  // Default estimates based on general categories
  if (lowerName.includes('hardware')) return 25.00;
  if (lowerName.includes('kit')) return 35.00;
  if (lowerName.includes('set')) return 30.00;
  
  return null;
}

/**
 * Validate estimate quality - "Lazy Estimator" Blocker
 * 
 * Enforces quality standards:
 * 1. Forbidden Phrases: Blocks single-word lazy item names
 * 2. Zero Dollar Check: Flags items with $0 unit cost
 * 3. Type Mismatch Check: Auto-fixes items with 'Labor' in name marked as 'MAT'
 */
export function validateEstimateQuality(items) {
  const errors = [];
  const warnings = [];
  const corrections = [];
  const correctedItems = [...items];
  
  correctedItems.forEach((item, index) => {
    const itemName = (item.itemName || '').trim();
    const lowerName = itemName.toLowerCase();
    const unitCost = parseFloat(item.unitCost) || 0;
    const costType = (item.costType || 'Material').toLowerCase();
    
    // Check for forbidden single-word phrases
    if (FORBIDDEN_PHRASES.includes(lowerName)) {
      const suggestion = LAZY_PHRASE_CORRECTIONS[lowerName];
      errors.push({
        index,
        type: 'forbidden_phrase',
        message: `"${itemName}" is too vague. Suggestion: ${suggestion}`,
        itemName,
        suggestion
      });
    }
    
    // Check for zero dollar items (warning only)
    if (unitCost === 0) {
      warnings.push({
        index,
        type: 'zero_cost',
        message: `"${itemName}" has $0 unit cost`,
        itemName
      });
    }
    
    // Auto-fix labor items marked as material
    if (costType === 'material' && lowerName.includes('labor')) {
      corrections.push({
        index,
        type: 'type_correction',
        message: `"${itemName}" changed from Material to Labor`,
        itemName,
        from: 'Material',
        to: 'Labor'
      });
      correctedItems[index] = { ...item, costType: 'Labor' };
    }
    
    // Auto-fix material items marked as labor
    if (costType === 'labor' && !lowerName.includes('labor') && !lowerName.includes('install')) {
      const isMaterial = MATERIAL_OVERRIDE_KEYWORDS.some(kw => lowerName.includes(kw));
      if (isMaterial) {
        corrections.push({
          index,
          type: 'type_correction',
          message: `"${itemName}" changed from Labor to Material`,
          itemName,
          from: 'Labor',
          to: 'Material'
        });
        correctedItems[index] = { ...item, costType: 'Material' };
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    corrections,
    correctedItems
  };
}

/**
 * Compute totals for a scope group
 */
export function computeGroupTotals(group) {
  const items = group.items || [];
  let materialCost = 0;
  let laborCost = 0;
  
  items.forEach(item => {
    const qty = parseFloat(item.quantity) || 0;
    const unitCost = parseFloat(item.unitCost) || 0;
    const total = qty * unitCost;
    
    if (item.costType === 'Labor') {
      laborCost += total;
    } else {
      materialCost += total;
    }
  });
  
  return {
    materialCost,
    laborCost,
    totalCost: materialCost + laborCost,
    itemCount: items.length
  };
}

/**
 * Compute totals for all scope groups
 * Returns both builder (cost) and client (with markup) totals
 */
export function computeScopeTotals(groups) {
  if (!groups || !Array.isArray(groups)) {
    return {
      materialCost: 0,
      laborCost: 0,
      subtotal: 0,
      itemCount: 0,
      builderTotal: 0,
      clientTotal: 0,
    };
  }
  
  let totalMaterial = 0;
  let totalLabor = 0;
  let totalItems = 0;
  let builderTotal = 0;
  let clientTotal = 0;
  
  groups.forEach(group => {
    const groupTotals = computeGroupTotals(group);
    totalMaterial += groupTotals.materialCost;
    totalLabor += groupTotals.laborCost;
    totalItems += groupTotals.itemCount;
    
    // Calculate builder (cost) and client (with markup) totals
    (group.items || []).forEach(item => {
      const qty = parseFloat(item.quantity) || 0;
      const unitCost = parseFloat(item.unitCost) || 0;
      const markupPercent = parseFloat(item.markupPercent) || 0;
      
      const itemCost = qty * unitCost;
      const itemClient = itemCost * (1 + markupPercent / 100);
      
      builderTotal += itemCost;
      clientTotal += itemClient;
    });
  });
  
  return {
    materialCost: totalMaterial,
    laborCost: totalLabor,
    subtotal: totalMaterial + totalLabor,
    itemCount: totalItems,
    builderTotal: builderTotal,
    clientTotal: clientTotal,
  };
}

/**
 * Build summary object from scope groups
 */
export function buildScopeSummary(groups) {
  const totals = computeScopeTotals(groups);
  
  return {
    ...totals,
    groupCount: groups.length,
    groups: groups.map(g => ({
      name: g.name,
      ...computeGroupTotals(g)
    }))
  };
}

/**
 * Format currency for display
 */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
}

/**
 * Format number with commas
 */
export function formatNumber(num, decimals = 0) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num || 0);
}

/**
 * Calculate markup percentage
 */
export function calculateMarkup(cost, sellPrice) {
  if (!cost || cost === 0) return 0;
  return ((sellPrice - cost) / cost) * 100;
}

/**
 * Apply markup to cost
 */
export function applyMarkup(cost, markupPercent) {
  return cost * (1 + markupPercent / 100);
}

/**
 * Calculate margin percentage
 */
export function calculateMargin(cost, sellPrice) {
  if (!sellPrice || sellPrice === 0) return 0;
  return ((sellPrice - cost) / sellPrice) * 100;
}

/**
 * Extract items from AI message bullet points
 * Used for Zero Amnesia protocol when AI describes items in text
 */
export function extractItemsFromMessage(message) {
  const items = [];
  if (!message) return items;
  
  // Pattern: "• Item Name - $XXX" or "- Item Name ($XXX)" or "* Item Name: $XXX"
  const patterns = [
    /[•\-\*]\s*([^$\n]+)\s*[\-–—:]\s*\$(\d+(?:,\d{3})*(?:\.\d{2})?)/gi,
    /[•\-\*]\s*([^(\n]+)\s*\(\$(\d+(?:,\d{3})*(?:\.\d{2})?)\)/gi,
    /(\d+)\.\s+([^$\n]+)\s*[\-–—:]\s*\$(\d+(?:,\d{3})*(?:\.\d{2})?)/gi,
  ];
  
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(message)) !== null) {
      const name = (match[1] || match[2] || '').trim();
      const cost = parseFloat((match[2] || match[3] || '0').replace(/,/g, ''));
      
      if (name && cost > 0) {
        // Determine cost type from name
        const isLabor = /labor|install|demo|removal|work|haul/i.test(name);
        
        items.push({
          itemName: name,
          unitCost: cost,
          quantity: 1,
          unit: 'EA',
          costType: isLabor ? 'Labor' : 'Material',
          category: 'General',
          markupPercent: 0,
        });
      }
    }
  }
  
  return items;
}

/**
 * Generate CLIENT BRIEF (Homeowner-focused, outcome-oriented)
 */
export function generateClientBrief(items, scopeDescription, totals, client) {
  const clientName = client?.name || client?.contact_name || "Valued Client";
  const projectAddress = client?.project_address || client?.address || "Your Property";
  
  let brief = `**CLIENT BRIEF**\n`;
  brief += `**Prepared for:** ${clientName}\n`;
  brief += `**Project Location:** ${projectAddress}\n`;
  brief += `**Date:** ${new Date().toLocaleDateString()}\n\n`;
  brief += `---\n\n`;
  brief += `**SCOPE SUMMARY**\n\n`;
  brief += `${scopeDescription}\n\n`;
  brief += `Our team will deliver professional-grade craftsmanship with attention to detail at every stage. `;
  brief += `We maintain clean, organized work areas and protect your home throughout the process.\n\n`;
  brief += `**WHAT TO EXPECT:**\n\n`;
  
  // Group items by category for cleaner presentation
  const categories = {};
  items.forEach(item => {
    const cat = item.group?.split(". ")[1] || "General Work";
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(item.name);
  });
  
  Object.entries(categories).forEach(([cat, itemNames]) => {
    brief += `• **${cat}:** ${itemNames.filter(Boolean).slice(0, 3).join(", ")}${itemNames.length > 3 ? ` and ${itemNames.length - 3} more items` : ""}\n`;
  });
  
  brief += `\n**QUALITY GUARANTEE:**\n`;
  brief += `All work performed to industry standards with manufacturer warranty compliance.\n`;
  brief += `Site will be cleaned daily. Final walkthrough included.\n\n`;
  brief += `---\n*Thank you for choosing us for your project.*`;
  
  return brief;
}

/**
 * Generate OPERATOR ORDER (Subcontractor-focused, technical, NO PRICING)
 */
export function generateOperatorOrder(items, groups, client) {
  const projectAddress = client?.project_address || client?.address || "Job Site";
  
  let order = `**OPERATOR ORDER**\n`;
  order += `**CLASSIFICATION:** WORK ORDER\n`;
  order += `**JOB SITE:** ${projectAddress}\n`;
  order += `**ISSUED:** ${new Date().toLocaleDateString()}\n\n`;
  order += `---\n\n`;
  order += `**EXECUTION REQUIREMENTS**\n\n`;
  
  groups.forEach(group => {
    const groupName = group.name?.split(". ")[1] || group.name || "GENERAL";
    order += `**${groupName.toUpperCase()}:**\n`;
    
    (group.items || []).forEach(item => {
      if (item.itemName) {
        order += `• ${item.itemName} — Qty: ${item.quantity} ${item.unit || "EA"}\n`;
        order += `  Install per manufacturer specifications. Verify field conditions.\n`;
      }
    });
    order += `\n`;
  });
  
  order += `**SITE PROTECTION PROTOCOL:**\n`;
  order += `• Deploy protective coverings on all adjacent surfaces\n`;
  order += `• Maintain dust barriers at all times\n`;
  order += `• NO materials left on site overnight without approval\n\n`;
  
  order += `**MANDATORY SITE CLEANUP:**\n`;
  order += `• Daily debris removal required\n`;
  order += `• Broom-clean condition at end of each work day\n`;
  order += `• Final cleanup includes all packaging and scrap material\n\n`;
  
  order += `---\n*Contractor Overwatch — Execute with precision.*`;
  
  return order;
}

/**
 * Create default scopes structure
 */
export function createDefaultScopes() {
  return {
    base: {
      id: "base",
      name: "BASE CONTRACT",
      type: "base",
      groups: [],
      locked: false,
    },
    changeOrders: [],
  };
}
